import React, { useState } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import {
  AlertTriangle,
  AlertCircle,
  Clock,
  Calendar,
  Sparkles,
  X,
  CreditCard,
  ArrowRight,
  ShieldAlert,
  Loader2,
} from "lucide-react";
import { PlanExpiryAlertData } from "../../../Redux/ApiCalls/Dashboard/SubscriptionAPI";

interface SubscriptionExpiryAlertModalProps {
  alertData: PlanExpiryAlertData;
  onClose: () => void;
  onRenew: () => void;
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(16px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.85; transform: scale(0.98); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(6px);
  animation: ${fadeIn} 0.2s ease-out;
`;

const ModalShell = styled.div`
  position: relative;
  width: 100%;
  max-width: 500px;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 24px 48px -12px rgba(15, 23, 42, 0.25), 0 0 0 1px rgba(15, 23, 42, 0.08);
  overflow: hidden;
  animation: ${slideUp} 0.25s cubic-bezier(0.16, 1, 0.3, 1);
`;

const ModalHero = styled.div<{ $severity: string }>`
  position: relative;
  padding: 24px 24px 20px;
  background: ${(p) => {
    if (p.$severity === "critical") {
      return "linear-gradient(135deg, rgba(239, 68, 68, 0.12) 0%, rgba(244, 63, 94, 0.08) 100%)";
    }
    if (p.$severity === "warning") {
      return "linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(201, 77, 170, 0.08) 100%)";
    }
    return "linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(201, 77, 170, 0.08) 100%)";
  }};
  border-bottom: 1px solid
    ${(p) => {
      if (p.$severity === "critical") return "rgba(239, 68, 68, 0.16)";
      if (p.$severity === "warning") return "rgba(245, 158, 11, 0.16)";
      return "rgba(99, 102, 241, 0.16)";
    }};
`;

const HeroRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const HeroText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const HeroBadge = styled.div<{ $severity: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  padding: 4px 10px;
  border-radius: 999px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: ${(p) => {
    if (p.$severity === "critical") return "rgba(239, 68, 68, 0.12)";
    if (p.$severity === "warning") return "rgba(245, 158, 11, 0.12)";
    return "rgba(99, 102, 241, 0.12)";
  }};
  color: ${(p) => {
    if (p.$severity === "critical") return "#dc2626";
    if (p.$severity === "warning") return "#d97706";
    return "#4f46e5";
  }};
  border: 1px solid
    ${(p) => {
      if (p.$severity === "critical") return "rgba(239, 68, 68, 0.25)";
      if (p.$severity === "warning") return "rgba(245, 158, 11, 0.25)";
      return "rgba(99, 102, 241, 0.25)";
    }};
`;

const ModalTitle = styled.h2<{ $severity: string }>`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: ${(p) => (p.$severity === "critical" ? "#dc2626" : "#171717")};
`;

const ModalSubtitle = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  color: rgba(23, 23, 23, 0.65);
  line-height: 1.45;
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid rgba(23, 23, 23, 0.08);
  background: rgba(255, 255, 255, 0.85);
  color: rgba(23, 23, 23, 0.55);
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;

  &:hover {
    background: #fff;
    color: #171717;
    transform: scale(1.05);
  }
`;

const ModalBody = styled.div`
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PlanDetailsCard = styled.div<{ $severity: string }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-radius: 14px;
  background: #f8fafc;
  border: 1px solid
    ${(p) => {
      if (p.$severity === "critical") return "rgba(239, 68, 68, 0.25)";
      if (p.$severity === "warning") return "rgba(245, 158, 11, 0.25)";
      return "rgba(99, 102, 241, 0.25)";
    }};
`;

const PlanHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const PlanNameGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PlanIconWrap = styled.div<{ $severity: string }>`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => {
    if (p.$severity === "critical") return "rgba(239, 68, 68, 0.12)";
    if (p.$severity === "warning") return "rgba(245, 158, 11, 0.12)";
    return "rgba(99, 102, 241, 0.12)";
  }};
  color: ${(p) => {
    if (p.$severity === "critical") return "#dc2626";
    if (p.$severity === "warning") return "#d97706";
    return "#4f46e5";
  }};
`;

const PlanTextInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const PlanName = styled.span`
  font-family: Urbanist, sans-serif;
  font-size: 16px;
  font-weight: 800;
  color: #171717;
`;

const PlanType = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;
  color: rgba(23, 23, 23, 0.55);
`;

const CountdownChip = styled.div<{ $severity: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 800;
  background: ${(p) => {
    if (p.$severity === "critical") return "#dc2626";
    if (p.$severity === "warning") return "#d97706";
    return "#4f46e5";
  }};
  color: #ffffff;
  animation: ${(p) => (p.$severity === "critical" ? pulse : "none")} 2s infinite ease-in-out;
`;

const PlanMetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px dashed rgba(23, 23, 23, 0.1);
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  color: rgba(23, 23, 23, 0.7);
`;

const MetaItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
`;

const MessageText = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.78);
  line-height: 1.55;
`;

const NoticeInfoBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(201, 77, 170, 0.06);
  border: 1px solid rgba(201, 77, 170, 0.15);
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  color: rgba(23, 23, 23, 0.72);
  line-height: 1.5;
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px 24px;
  background: #ffffff;
`;

const DismissButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 0 18px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  background: #ffffff;
  color: rgba(23, 23, 23, 0.7);
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #f8fafc;
    color: #171717;
  }
`;

const RenewButton = styled.button<{ $severity: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 44px;
  padding: 0 22px;
  border: none;
  border-radius: 12px;
  background: ${(p) => {
    if (p.$severity === "critical") {
      return "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)";
    }
    return "linear-gradient(144.01deg, rgba(255, 201, 81, 1) 0%, rgba(206, 86, 176, 1) 100%)";
  }};
  color: #ffffff;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 20px
    ${(p) => (p.$severity === "critical" ? "rgba(239, 68, 68, 0.3)" : "rgba(201, 77, 170, 0.28)")};
  transition: all 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px
      ${(p) =>
        p.$severity === "critical" ? "rgba(239, 68, 68, 0.38)" : "rgba(201, 77, 170, 0.36)"};
  }
`;

export const SubscriptionExpiryAlertModal: React.FC<SubscriptionExpiryAlertModalProps> = ({
  alertData,
  onClose,
  onRenew,
}) => {
  const severity = alertData.severity || (alertData.stage !== undefined && alertData.stage !== null && alertData.stage <= 1 ? "critical" : alertData.stage !== null && alertData.stage !== undefined && alertData.stage <= 7 ? "warning" : "info");
  const daysLeft = alertData.days_left ?? alertData.stage ?? 0;

  const getCountdownLabel = () => {
    if (daysLeft === 0 || alertData.stage === 0) return "Expires Today!";
    if (daysLeft === 1) return "1 Day Left";
    return `${daysLeft} Days Left`;
  };

  const getTitle = () => {
    if (severity === "critical") {
      if (daysLeft === 0 || alertData.stage === 0) return "Your Subscription Expires Today";
      return "Your Subscription Expires in 1 Day";
    }
    if (severity === "warning") {
      return `Subscription Expiring in ${daysLeft} Days`;
    }
    return "Upcoming Subscription Renewal";
  };

  const getBadgeLabel = () => {
    if (severity === "critical") return "Critical Expiry Alert";
    if (severity === "warning") return "Subscription Warning";
    return "Plan Renewal Notice";
  };

  return createPortal(
    <ModalOverlay id="subscription-expiry-alert-overlay" onClick={onClose}>
      <ModalShell onClick={(e) => e.stopPropagation()}>
        <ModalHero $severity={severity}>
          <HeroRow>
            <HeroText>
              <HeroBadge $severity={severity}>
                {severity === "critical" ? (
                  <AlertCircle size={12} />
                ) : severity === "warning" ? (
                  <AlertTriangle size={12} />
                ) : (
                  <Clock size={12} />
                )}
                {getBadgeLabel()}
              </HeroBadge>
              <ModalTitle $severity={severity}>{getTitle()}</ModalTitle>
              <ModalSubtitle>
                Renew your plan to maintain uninterrupted access to your studio dashboard.
              </ModalSubtitle>
            </HeroText>
            <CloseBtn
              id="subscription-expiry-alert-close"
              type="button"
              onClick={onClose}
              aria-label="Close"
            >
              <X size={18} />
            </CloseBtn>
          </HeroRow>
        </ModalHero>

        <ModalBody>
          <PlanDetailsCard $severity={severity}>
            <PlanHeaderRow>
              <PlanNameGroup>
                <PlanIconWrap $severity={severity}>
                  {severity === "critical" ? (
                    <ShieldAlert size={18} />
                  ) : (
                    <CreditCard size={18} />
                  )}
                </PlanIconWrap>
                <PlanTextInfo>
                  <PlanName>{alertData.plan_name ? `${alertData.plan_name} Plan` : "Current Plan"}</PlanName>
                  <PlanType>{alertData.plan_type ? `${alertData.plan_type} billing` : "Active Subscription"}</PlanType>
                </PlanTextInfo>
              </PlanNameGroup>

              <CountdownChip $severity={severity}>
                <Clock size={13} />
                {getCountdownLabel()}
              </CountdownChip>
            </PlanHeaderRow>

            {alertData.expiry_date && (
              <PlanMetaRow>
                <MetaItem>
                  <Calendar size={13} style={{ color: "#c94daa" }} />
                  <span>
                    <strong>Expiry Date:</strong> {alertData.expiry_date}
                  </span>
                </MetaItem>
              </PlanMetaRow>
            )}
          </PlanDetailsCard>

          <MessageText>
            {alertData.message ||
              `Your ${alertData.plan_name || "current"} plan is expiring soon. Renew now to prevent any service interruption.`}
          </MessageText>

          <NoticeInfoBox>
            <Sparkles size={16} style={{ color: "#c94daa", flexShrink: 0, marginTop: 1 }} />
            <span>
              Renewing before expiry extends your plan from the expiry date — you will not lose any active days.
            </span>
          </NoticeInfoBox>
        </ModalBody>

        <ModalFooter>
          <DismissButton id="subscription-expiry-dismiss-btn" type="button" onClick={onClose}>
            Remind Me Later
          </DismissButton>
          <RenewButton
            id="subscription-expiry-renew-btn"
            type="button"
            $severity={severity}
            onClick={onRenew}
          >
            Renew Plan Now <ArrowRight size={15} />
          </RenewButton>
        </ModalFooter>
      </ModalShell>
    </ModalOverlay>,
    document.body
  );
};
